import type { ScheduleMeetingDto } from '../../../types/lobbyDay'

const PREFIX = 'lobby-schedule-draft:'

export function draftKey(eventId: string): string {
  return `${PREFIX}${eventId}`
}

/** Order-independent digest of the server's meetings. A draft is only restorable
 *  while this still matches, so we never replay stale edits over someone else's changes.
 *
 *  Field set intentionally covers only the server-editable fields the scheduler can currently
 *  change: id, legislatorId, askId, meetingDate, startTime, endTime, room, status, withWhom
 *  (editable day-of via the "Meeting with" select), assignments.
 *  The fields packetNotes and notes are deliberately excluded because no edit affordance exists
 *  for them on an existing meeting (only at creation time). Out-of-band day-of fields (attendance,
 *  role, staff, outcome) are not schedule-save fields and stay out too.
 *  If future changes add editing for any excluded save field, this digest MUST be updated
 *  to include it, or a stale draft will silently overwrite the newer server value on restore. */
export function serverFingerprint(meetings: ScheduleMeetingDto[]): string {
  return [...meetings]
    .map((m) => JSON.stringify({
      id: m.id, legislatorId: m.legislatorId, askId: m.askId, meetingDate: m.meetingDate,
      startTime: m.startTime, endTime: m.endTime, room: m.room, status: m.status, withWhom: m.withWhom,
      assignments: [...m.assignments].map((a) => a.assignmentId).sort(),
    }))
    .sort()
    .join('|')
}

export function serializeDraft(meetings: ScheduleMeetingDto[], fingerprint: string): string {
  return JSON.stringify({ fingerprint, meetings })
}

export function parseDraft(
  raw: string | null, currentFingerprint: string,
): ScheduleMeetingDto[] | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.fingerprint !== currentFingerprint) return null
    if (!Array.isArray(parsed.meetings)) return null
    return parsed.meetings as ScheduleMeetingDto[]
  } catch {
    return null
  }
}
